"use client"
import { Avatar, AvatarProps, Box, rem, Tooltip, TooltipProps } from "@mantine/core"
import { useCallback, useEffect, useMemo, useState } from "react"
import classes from "./CompanyAvatar.module.css"

interface Props {
  company: {
    url?: string
    email?: string
    name: string
  }
  tooltipProps?: TooltipProps
  avatarProps?: AvatarProps
}

export function CompanyAvatar({ avatarProps, tooltipProps, company }: Props) {
  const isValidHttpUrl = useCallback((string: string) => {
    let url

    try {
      url = new URL(`https://${string}`)
    } catch (_) {
      return false
    }

    return url.protocol === "http:" || url.protocol === "https:"
  }, [])

  const getDomain = useCallback((url: string, subdomain?: boolean) => {
    let _subdomain = subdomain || false

    let _url = url.replace(/(https?:\/\/)?(www.)?/i, "")

    if (!_subdomain) {
      const splittedURL = _url.split(".")

      _url = splittedURL.slice(splittedURL.length - 2).join(".")
    }

    if (_url.indexOf("/") !== -1) {
      return _url.split("/")[0]
    }

    return _url
  }, [])

  const companyDomain: string | undefined = useMemo(() => {
    if (company?.url) {
      return getDomain(company.url)
    }

    if (company?.email) {
      const emailDomain = company.email.split("@")[1]
      if (emailDomain) {
        return emailDomain
      }
    }

    if (isValidHttpUrl(company.name)) {
      return getDomain(company.name)
    }

    return undefined
  }, [company.email, company.name, company.url, getDomain, isValidHttpUrl])

  const companyLogoURL = useMemo(() => {
    if (companyDomain) {
      return `https://cdn.brandfetch.io/${companyDomain}/w/256/h/256?c=1id72UB6j4DLK2PZEK4` // TODO: Set this api key to a variable
    }

    return null
  }, [companyDomain])

  const [shouldShowCompanyLogo, updateShouldShowCompanyLogo] = useState(false)

  useEffect(() => {
    if (!companyLogoURL) {
      updateShouldShowCompanyLogo(false)
      return
    }

    fetch(companyLogoURL, {
      mode: "no-cors",
    })
      .then(() => {
        updateShouldShowCompanyLogo(true)
      })
      .catch(() => {
        updateShouldShowCompanyLogo(false)
      })
  }, [companyLogoURL])

  return (
    <Tooltip withArrow position="right" {...tooltipProps} label={company?.name}>
      <Box className={classes.avatarContainer}>
        <Avatar
          size={rem(14)}
          radius="sm"
          {...avatarProps}
          src={shouldShowCompanyLogo ? companyLogoURL : null}
          alt={company.name}
          {...(!shouldShowCompanyLogo && {
            name: company.name,
            color: "initials",
          })}
        />
      </Box>
    </Tooltip>
  )
}
